// @flow
import { isEmpty } from 'lodash';
import moment from 'moment';
import { logVmPlayerEvent } from 'Common/analytics/vmanalytics/actions';
import type { VmEventType, VmEvent } from 'Common/analytics/vmanalytics/types';
import { isSlingSourceType, SourceType as SourceTypeEnum } from 'Common/models/franchise';
import type { SourceType } from 'Common/models/franchise';
import BaseCastAnalytics from './BaseCastAnalytics';

class VmCastAnalytics extends BaseCastAnalytics {
  timeSeekStarted: number;
  lastSourceType: SourceType = SourceTypeEnum.NONE;
  lastEventPayload: VmEvent;

  onCafEvent(event: Object) {
    switch (event.type) {
      case 'PLAYER_LOAD_COMPLETE': {
        if (
          isSlingSourceType(this.lastSourceType) &&
          !isSlingSourceType(this.sourceType) &&
          !isEmpty(this.lastEventPayload)
        ) {
          this._logSwitchAway();
        }
        this.lastSourceType = this.sourceType;
        this._logVmAnalytics('startStreaming');
        break;
      }
      case 'PLAYING': {
        this._logVmAnalytics('ongoingStreaming');
        break;
      }
      case 'REQUEST_PLAY': {
        this._logVmAnalytics('resume');
        break;
      }
      case 'REQUEST_PAUSE':
        this._logVmAnalytics('pause');
        break;

      case 'REQUEST_SEEK': {
        this.timeSeekStarted = this.playbackTimeInMs;
        break;
      }
      case 'SEEKED': {
        const currentPlaybackTime = this.playbackTimeInMs;
        this._logVmAnalytics(currentPlaybackTime > this.timeSeekStarted ? 'seekFwd' : 'seekBack');
        break;
      }

      case 'MEDIA_FINISHED':
      case 'ENDED':
      case 'ERROR':
        if (isSlingSourceType(this.sourceType)) {
          this._logVmAnalytics('switchAway');
        }
        this._logVmAnalytics('endStreaming');
        break;
      default:
        break;
    }
  }

  _getUnixTimeStr = (time: Object | string) =>
    moment(time || '')
      .unix()
      .toString();

  _getVmEvent = (event: VmEventType) => {
    const {
      echostar3_id: echostarId = '',
      content_identification: contentIdentification = {},
      airings = [],
    } = this.eventFranchiseData || {};
    const { start_date_time: startDateTime, end_date_time: endDateTime } = !isEmpty(airings)
      ? airings[0]
      : {};
    const timeElapse = this.playbackTimeInMs.toString();

    const { content_type: echostarContentType = '', series_id: echostarSeriesId = '' } =
      contentIdentification || {};

    this.lastEventPayload = {
      event,
      contentType: this.sourceType,
      startTime: this._getUnixTimeStr(startDateTime),
      endTime: this._getUnixTimeStr(endDateTime),
      timeElapse,
      channelNumber: this.channelNumber,
      channelName: this.channelName,
      progamName: this.showName,
      airTime: this._getUnixTimeStr(this.dateAndTimeOfTvAiring),
      episodeTitle: this.episodeTitle,
      timeFromStart: timeElapse,
      echostarId,
      echostarContentType,
      echostarSeriesId,
      echostarEpisodeId: this.episodeId,
    };

    return this.lastEventPayload;
  };

  _logVmAnalytics = (event: VmEventType) => {
    const eventPayload = this._getVmEvent(event);
    this.store.dispatch(logVmPlayerEvent(eventPayload));
  };

  _logSwitchAway = () =>
    this.store.dispatch(logVmPlayerEvent({ ...this.lastEventPayload, event: 'switchAway' }));

  onSlingMediaInfoChanged = () => {
    this._logSwitchAway();
    this._logVmAnalytics('switchProgram');
  };

  onCafShutdown() {
    if (isSlingSourceType(this.sourceType)) {
      this._logVmAnalytics('switchAway');
    }
    this._logVmAnalytics('endStreaming');
  }
}

export default VmCastAnalytics;
